import * as O from 'fp-ts/lib/Option';
import {CommissionScheduleDto} from 'Agent/Commissions/api';
import {CommissionSchedule} from 'Agent/Commissions/models/CommissionSchedule';
import {AsyncPage, Empty} from 'Core/models/Async';
import {FilterOptionUnion, FilterOptionValue} from 'Core/models/FilterOption';
import {Page} from 'Core/models/Page';
import {AgencyFilters} from 'Shared/models/AgencyFilters';
import {createAction} from 'Shared/utilities/actionCreator';
import {ScheduleDetails} from 'Agent/Commissions/models/ScheduleDetails';
import {Sort} from 'Shared/components/PagedView/Table/Sort';

export enum CommissionSchedulesActionType {
  GET_SCHEDULES = 'commissionSchedules/GET_SCHEDULES',
  CHANGE_SCHEDULES = 'commissionSchedules/CHANGE_SCHEDULES',
  CHANGE_FILTERS = 'commissionSchedules/CHANGE_FILTERS',
  CLEAR_FILTERS = 'commissionSchedules/CLEAR_FILTERS',
  CLEAR = 'commissionSchedules/CLEAR',
  LOAD_DETAILS = 'commissionSchedules/LOAD_DETAILS',
  CLEAR_DETAILS = 'commissionSchedules/CLEAR_DETAILS',
  CHANGE_SORT = 'commissionSchedules/CHANGE_SORT',
  CLEAR_SORT = 'commissionSchedules/CLEAR_SORT',
}

export type CommissionScheduleFilter = {
  agentName: string;
  agentNumber: string;
  contract: string;
  sourceId: FilterOptionUnion;
};

export const EmptyCommissionScheduleFilter = {
  agentName: '',
  agentNumber: '',
  contract: '',
  sourceId: FilterOptionValue.EmptyOption(),
};

export const clearDetails = () => createAction(CommissionSchedulesActionType.CLEAR_DETAILS);
export const loadDetails = (details: ScheduleDetails) =>
  createAction(CommissionSchedulesActionType.LOAD_DETAILS, {details});
export const clearFilters = () => createAction(CommissionSchedulesActionType.CLEAR_FILTERS);
export const clear = () => createAction(CommissionSchedulesActionType.CLEAR);
export const changeSort = (sort?: Sort) => createAction(CommissionSchedulesActionType.CHANGE_SORT, {sort});
export const clearSort = () => createAction(CommissionSchedulesActionType.CLEAR_SORT);
export const getCommissionSchedules = (
  filter: CommissionScheduleFilter,
  agencyFilters: AgencyFilters,
  page?: Page<CommissionSchedule>,
  sort?: Sort
) => createAction(CommissionSchedulesActionType.GET_SCHEDULES, {page, filter, agencyFilters, sort});
export const changeCommissionSchedules = (schedules: AsyncPage<Page<CommissionSchedule>>) =>
  createAction(CommissionSchedulesActionType.CHANGE_SCHEDULES, {schedules});
export const changeFilter = (filter: CommissionScheduleFilter) =>
  createAction(CommissionSchedulesActionType.CHANGE_FILTERS, {filter});

type CommissionSchedulesState = {
  page: AsyncPage<Page<CommissionScheduleDto>>;
  filter: CommissionScheduleFilter;
  details: O.Option<ScheduleDetails>;
  sort: O.Option<Sort>;
};

const DEFAULT_STATE: CommissionSchedulesState = {
  page: new Empty(),
  filter: EmptyCommissionScheduleFilter,
  details: O.none,
  sort: O.none,
};
type CommissionSchedulesAction =
  | ReturnType<typeof loadDetails>
  | ReturnType<typeof clearDetails>
  | ReturnType<typeof changeCommissionSchedules>
  | ReturnType<typeof changeFilter>
  | ReturnType<typeof clear>
  | ReturnType<typeof clearFilters>
  | ReturnType<typeof changeSort>
  | ReturnType<typeof clearSort>;

export default (
  state: CommissionSchedulesState = DEFAULT_STATE,
  action: CommissionSchedulesAction
): CommissionSchedulesState => {
  switch (action.type) {
    case CommissionSchedulesActionType.CLEAR_DETAILS:
      return {
        ...state,
        details: DEFAULT_STATE.details,
      };
    case CommissionSchedulesActionType.LOAD_DETAILS:
      return {
        ...state,
        details: O.some(action.payload.details),
      };
    case CommissionSchedulesActionType.CLEAR:
      return DEFAULT_STATE;
    case CommissionSchedulesActionType.CLEAR_FILTERS:
      return {
        ...state,
        filter: DEFAULT_STATE.filter,
      };
    case CommissionSchedulesActionType.CHANGE_FILTERS:
      return {
        ...state,
        filter: action.payload.filter,
      };
    case CommissionSchedulesActionType.CHANGE_SCHEDULES:
      return {
        ...state,
        page: action.payload.schedules,
      };
    case CommissionSchedulesActionType.CLEAR_SORT:
      return {
        ...state,
        sort: DEFAULT_STATE.sort,
      };
    case CommissionSchedulesActionType.CHANGE_SORT:
      return {
        ...state,
        sort: O.fromNullable(action.payload.sort),
      };
    default:
      return state;
  }
};
